import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Table, Tag, Space, Typography, Button as AntButton, Card as AntCard, Empty, Grid, Input, Tooltip, Badge, Select, Pagination } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import WidgetSectionTitle from 'widgets/WidgetSectionTitle';
import paths from 'routes/paths';
import EmailProvider from './EmailProvider';
import useEmails from 'hooks/emails/useEmails';
import { useEmailContext } from './EmailProvider';
import useFormattedDate from 'hooks/useFormatDate';

const SentEmailsList = () => {
  const navigate = useNavigate();
  const { emailState, emailDispatch } = useEmailContext();
  const { getSentEmails, loading, error } = useEmails();
  const { formatDateTime } = useFormattedDate();
  const sentEmails = Array.isArray(emailState.sentEmails) ? emailState.sentEmails : [];
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [pageSize, setPageSize] = useState(25);
  const [mobilePage, setMobilePage] = useState(1);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const loadSentEmails = useCallback(async () => {
    try {
      const list = await getSentEmails();
      emailDispatch({ type: 'SET_SENT_EMAILS', payload: list });
    } catch (err) {
      toast.error(err.message || 'Failed to load sent emails');
    }
  }, [getSentEmails, emailDispatch]);

  useEffect(() => {
    loadSentEmails();
  }, [loadSentEmails]);

  const handleView = (email) => {
    const id = email.id ?? email.messageId;
    if (id != null) navigate(paths.emailSentDetail(id));
  };

  const dataSource = useMemo(() => (
    sentEmails.map((email, index) => {
      const id = email.id ?? email.messageId;
      return {
        key: id ?? `sent-${index}`,
        id,
        subject: email.subject ?? email.title ?? 'Untitled',
        preview: (email.body ?? '').replace(/\s+/g, ' ').trim(),
        recipients: (email.to ?? email.recipients ?? []).join(', '),
        recipientsList: email.to ?? email.recipients ?? [],
        status: email.emailStatus ?? email.status ?? 'SENT',
        sentAt: formatDateTime(email.sentAt),
        sentAtRaw: email.sentAt ? new Date(email.sentAt).getTime() : 0,
        raw: email
      };
    })
  ), [sentEmails, formatDateTime]);

  const statusOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Failed', value: 'FAILED' },
    { label: 'Received', value: 'RECEIVED' },
    { label: 'Processed', value: 'PROCESSED' },
    { label: 'Processing', value: 'PROCESSING' }
  ];

  const filteredDataSource = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return dataSource.filter((item) => {
      if (statusFilter !== 'ALL' && String(item.status || '').toUpperCase() !== statusFilter) {
        return false;
      }
      if (!normalized) return true;
      return (
        item.subject.toLowerCase().includes(normalized)
        || item.preview.toLowerCase().includes(normalized)
        || item.recipients.toLowerCase().includes(normalized)
        || String(item.status).toLowerCase().includes(normalized)
      );
    });
  }, [dataSource, query, statusFilter]);

  const mobileDataSource = useMemo(() => {
    const start = (mobilePage - 1) * pageSize;
    const end = start + pageSize;
    return filteredDataSource.slice(start, end);
  }, [filteredDataSource, mobilePage, pageSize]);

  useEffect(() => {
    setMobilePage(1);
  }, [query, statusFilter, pageSize]);

  const getStatusTag = (value) => {
    const status = String(value || 'SENT').toUpperCase();
    const color =
      status === 'FAILED'
        ? 'error'
        : status === 'PENDING'
          ? 'warning'
          : status === 'PROCESSING'
            ? 'processing'
            : status === 'RECEIVED'
              ? 'default'
              : 'success';
    return <Tag color={color}>{status}</Tag>;
  };

  const columns = [
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: '35%',
      render: (_, record) => (
        <div className="gmail-subject-cell">
          <Typography.Text strong>{record.subject}</Typography.Text>
          <div className="text-muted small text-truncate">{record.preview || 'No content'}</div>
        </div>
      ),
      ellipsis: true
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      width: '30%',
      responsive: ['md'],
      render: (value) => (
        <Typography.Text type="secondary">
          {value || '—'}
        </Typography.Text>
      ),
      ellipsis: true
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      responsive: ['sm'],
      render: (value) => getStatusTag(value)
    },
    {
      title: 'Delivered At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: '25%',
      responsive: ['md'],
      sorter: (a, b) => a.sentAtRaw - b.sentAtRaw,
      defaultSortOrder: 'descend'
    },
   
  ];

  if (error && !sentEmails.length && !loading) {
    return (
      <Alert variant="danger">{error}</Alert>
    );
  }

  return (
    <div className="gmail-table-shell">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 gmail-toolbar">
        <Space size={8}>
          <Tooltip title="Refresh">
            <AntButton
              size="small"
              className="gmail-icon-btn"
              onClick={loadSentEmails}
              icon={<ReloadOutlined />}
              disabled={loading}
            />
          </Tooltip>
        </Space>
        <div className="ms-auto d-flex align-items-center gap-2 flex-wrap gmail-table-controls">
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            className="gmail-status-filter"
          />
          <Input
            allowClear
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            prefix={<SearchOutlined />}
            placeholder="Search sent mail"
            className="gmail-search"
          />
          <Badge count={filteredDataSource.length} showZero className="gmail-count" />
        </div>
      </div>
      {isMobile ? (
        <>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : !filteredDataSource.length ? (
            <Empty description="No sent emails" />
          ) : (
            <div className="d-flex flex-column gap-2">
              {mobileDataSource.map((item) => (
                <div key={item.key} className="px-0">
              <AntCard className="w-100 shadow-sm border-0 gmail-mobile-card" size="small">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div style={{ minWidth: 0 }}>
                    <Typography.Text strong className="d-block text-truncate">
                      {item.subject}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="d-block text-truncate">
                      {item.recipients || '—'}
                    </Typography.Text>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {getStatusTag(item.status)}
                  </div>
                </div>
                <div className="mt-2 text-muted small text-truncate">
                  {item.preview || 'No content'}
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <Typography.Text type="secondary" className="small">
                    {item.sentAt}
                  </Typography.Text>
                  <AntButton size="small" type="link" onClick={() => handleView(item.raw)}>
                    View
                  </AntButton>
                </div>
              </AntCard>
                </div>
              ))}
            </div>
          )}
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              current={mobilePage}
              pageSize={pageSize}
              total={filteredDataSource.length}
              showSizeChanger
              pageSizeOptions={['10', '25', '50', '100']}
              onChange={(page, size) => {
                setMobilePage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              }}
              hideOnSinglePage
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
            />
          </div>
        </>
      ) : (
        <Table
          dataSource={filteredDataSource}
          columns={columns}
          loading={loading}
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            hideOnSinglePage: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
          }}
          onChange={(pagination) => {
            if (pagination?.pageSize) setPageSize(pagination.pageSize);
          }}
          locale={{ emptyText: <Empty description="No sent emails" /> }}
          tableLayout="fixed"
          rowClassName="gmail-row"
          onRow={(record) => ({
            onClick: (event) => {
              const target = event.target;
              if (target.closest('button, a, input, .ant-checkbox-wrapper')) return;
              handleView(record.raw);
            }
          })}
        />
      )}
    </div>
  );
};

const SentEmailsPage = () => {
  const navigate = useNavigate();

  return (
    <EmailProvider>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
          <WidgetSectionTitle
            icon="paper-plane"
            title="Sent Emails"
            subtitle="View and manage emails you have sent"
            className="mb-0"
          />
          <AntButton type="primary" onClick={() => navigate(paths.emailCompose)}>
            <FontAwesomeIcon icon="plus" className="me-2" />
            Compose Email
          </AntButton>
        </div>
        <SentEmailsList />
      </Container>
    </EmailProvider>
  );
};

export default SentEmailsPage;
